<template>
    <div class="p-3">
        <div class="d-flex flex-wrap align-items-center justify-content-between gap-2 mb-2">
            <div>
                <h3 class="mb-0">Riwayat Pasien</h3>
                <small class="text-muted">
                    Pilih pasien untuk melihat seluruh riwayat pelayanan dan pemeriksaannya.
                </small>
            </div>
        </div>
        <hr />

        <div class="card border-0 shadow-sm mb-3">
            <div class="card-header bg-white fw-semibold d-flex align-items-center gap-2">
                <i class="fa fa-filter text-success"></i> Pencarian Data
            </div>

            <div class="card-body">
                <div class="row g-3">
                    <div class="col-12 col-lg-6">
                        <label class="form-label small fw-semibold text-muted">Unit Tindakan</label>
                        <div class="btn-group w-100" role="group">
                            <template v-for="unit in unit_tindakan_options">
                                <input
                                    :key="`unit-input-${unit.value}`"
                                    type="radio"
                                    class="btn-check"
                                    name="unit_tindakan"
                                    :id="`unit-${unit.value}`"
                                    :value="unit.value"
                                    v-model="filter.unit_tindakan"
                                />
                                <label
                                    :key="`unit-label-${unit.value}`"
                                    class="btn btn-outline-success"
                                    :for="`unit-${unit.value}`"
                                >
                                    {{ unit.label }}
                                </label>
                            </template>
                        </div>
                    </div>

                    <div class="col-12 col-md-6 col-lg-3">
                        <label for="rp_poli" class="form-label small fw-semibold text-muted">Poli</label>
                        <v-select
                            id="rp_poli"
                            v-model="selected.poli"
                            :options="options.poli"
                            :filterable="false"
                            label="label"
                            placeholder="Semua poli"
                            @search="searchPoli"
                            @open="searchPoli('')"
                            @input="onPoliChange"
                        >
                            <template #no-options>Ketik untuk mencari poli...</template>
                        </v-select>
                    </div>

                    <div class="col-12 col-md-6 col-lg-3">
                        <label for="rp_dokter" class="form-label small fw-semibold text-muted">Dokter</label>
                        <v-select
                            id="rp_dokter"
                            v-model="selected.dokter"
                            :options="options.dokter"
                            :filterable="false"
                            label="label"
                            placeholder="Semua dokter"
                            @search="searchDokter"
                            @open="searchDokter('')"
                            @input="onDokterChange"
                        >
                            <template #no-options>Ketik untuk mencari dokter...</template>
                        </v-select>
                    </div>

                    <div class="col-12 col-lg-6">
                        <label class="form-label small fw-semibold text-muted">Tanggal Registrasi</label>
                        <div class="input-group">
                            <input
                                type="date"
                                class="form-control"
                                v-model="filter.start_date"
                                aria-label="Tanggal awal"
                            />
                            <span class="input-group-text">s/d</span>
                            <input
                                type="date"
                                class="form-control"
                                v-model="filter.end_date"
                                aria-label="Tanggal akhir"
                            />
                        </div>
                        <div v-if="invalid_date_range" class="form-text text-danger">
                            Tanggal akhir harus sama atau setelah tanggal awal.
                        </div>
                    </div>

                    <div class="col-12 col-lg-6">
                        <label for="cari_pasien" class="form-label small fw-semibold text-muted">
                            Cari Pasien
                        </label>
                        <div class="input-group">
                            <span class="input-group-text"><i class="fa fa-search"></i></span>
                            <input
                                id="cari_pasien"
                                type="text"
                                class="form-control"
                                placeholder="No. Registrasi / No. RM / Nama Pasien"
                                v-model="filter.cari_pasien"
                                @keyup.enter="applyFilter"
                            />
                        </div>
                    </div>
                </div>

                <div class="d-flex justify-content-end gap-2 mt-3">
                    <button class="btn btn-outline-danger" @click="resetFilter" :disabled="is_loading">
                        <i class="fa fa-refresh"></i> Reset
                    </button>
                    <button class="btn btn-primary" @click="applyFilter" :disabled="is_loading">
                        <i class="fa" :class="is_loading ? 'fa-spinner fa-spin' : 'fa-search'"></i>
                        {{ is_loading ? "Memuat..." : "Cari" }}
                    </button>
                </div>
            </div>
        </div>

        <div class="card border-0 shadow-sm">
            <div
                class="card-header bg-white fw-semibold d-flex align-items-center justify-content-between"
            >
                <span><i class="fa fa-list-ul text-success me-1"></i> Daftar Pasien</span>
                <span v-if="active_filter_labels.length" class="d-flex flex-wrap gap-1">
                    <span
                        v-for="(label, index) in active_filter_labels"
                        :key="index"
                        class="badge bg-light text-dark border fw-normal"
                    >
                        {{ label }}
                    </span>
                </span>
            </div>

            <div class="card-body position-relative">
                <div v-if="is_loading" class="table-loading">
                    <div class="spinner-border text-success" role="status"></div>
                    <div class="mt-2 fw-semibold text-muted">Memuat data...</div>
                </div>

                <div class="table-responsive-wrapper">
                    <div id="table-riwayat-pasien"></div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import vSelect from "vue-select";
import "vue-select/dist/vue-select.css";
import { initializeDataTable } from "@/utils/tableHelper";

const defaultFilter = () => ({
    unit_tindakan: 3,
    poli: "",
    dokter: "",
    start_date: "",
    end_date: "",
    cari_pasien: "",
});

export default {
    name: "RiwayatPasien",

    components: {
        "v-select": vSelect,
    },

    data() {
        return {
            table: null,
            is_loading: false,
            filter: defaultFilter(),
            selected: { poli: null, dokter: null },
            options: { poli: [], dokter: [] },
            unit_tindakan_options: [
                { value: 1, label: "Rawat Jalan" },
                { value: 2, label: "Rawat Inap" },
                { value: 3, label: "Keduanya" },
            ],
        };
    },

    computed: {
        invalid_date_range() {
            const { start_date, end_date } = this.filter;
            return Boolean(start_date && end_date && end_date < start_date);
        },

        active_filter_labels() {
            const labels = [];
            const unit = this.unit_tindakan_options.find(
                (item) => item.value == this.filter.unit_tindakan
            );

            if (unit && unit.value != 3) labels.push(unit.label);
            if (this.selected.poli) labels.push(this.selected.poli.label);
            if (this.selected.dokter) labels.push(this.selected.dokter.label);
            if (this.filter.start_date || this.filter.end_date) {
                labels.push(`${this.filter.start_date || "…"} s/d ${this.filter.end_date || "…"}`);
            }
            if (this.filter.cari_pasien) labels.push(`"${this.filter.cari_pasien}"`);

            return labels;
        },
    },

    created() {
        this.searchPoli = _.debounce(this.fetchPoli, 350);
        this.searchDokter = _.debounce(this.fetchDokter, 350);
    },

    mounted() {
        this.$nextTick(this.initTable);
    },

    beforeDestroy() {
        if (this.table) {
            this.table.destroy();
            this.table = null;
        }
    },

    methods: {
        buildApiUrl() {
            const params = new URLSearchParams();

            Object.entries(this.filter).forEach(([key, value]) => {
                if (value !== null && value !== undefined && value !== "") {
                    params.set(key, value);
                }
            });

            return `/api/riwayat-pasien/list-pasien?${params.toString()}`;
        },

        initTable() {
            const element = document.getElementById("table-riwayat-pasien");
            if (!element) return;

            this.table = initializeDataTable(
                element,
                {
                    buildApiUrl: this.buildApiUrl,
                    paginationSize: 25,
                    paginationSizeSelector: [10, 25, 50, 100],
                    placeholder: "Tidak ada pasien yang cocok dengan filter.",
                    columnDefaults: { headerSort: false, vertAlign: "middle" },
                    dataLoader: false,
                    columns: [
                        { title: "No. Registrasi", field: "Regno", width: 140 },
                        { title: "No. RM", field: "Medrec", width: 110 },
                        { title: "Nama Pasien", field: "Firstname", minWidth: 180 },
                        {
                            title: "Tanggal Registrasi",
                            field: "Regdate",
                            width: 160,
                            formatter: (cell) => this.formatTanggal(cell.getValue()),
                        },
                        {
                            title: "Poli / Ruang Rawat",
                            minWidth: 200,
                            formatter: (cell) => {
                                const row = cell.getRow().getData();
                                const poli = row.poli ? row.poli.NMPoli : "-";
                                const bangsal =
                                    row.fppri && row.fppri.bangsal
                                        ? row.fppri.bangsal.NmBangsal
                                        : null;

                                return bangsal ? `${poli} / ${bangsal}` : poli;
                            },
                        },
                        {
                            title: "Dokter",
                            minWidth: 180,
                            formatter: (cell) => {
                                const row = cell.getRow().getData();
                                if (row.fppri && row.fppri.NmDocRS) return row.fppri.NmDocRS;
                                return row.dokter ? row.dokter.NmDoc : "-";
                            },
                        },
                        {
                            title: "Unit Tindakan",
                            width: 140,
                            hozAlign: "center",
                            formatter: (cell) => {
                                const ranap = Boolean(cell.getRow().getData().fppri);
                                const cls = ranap ? "bg-warning text-dark" : "bg-info text-dark";
                                const text = ranap ? "Rawat Inap" : "Rawat Jalan";

                                return `<span class="badge ${cls}">${text}</span>`;
                            },
                        },
                        {
                            title: "Aksi",
                            width: 110,
                            hozAlign: "center",
                            formatter: () =>
                                '<button class="btn btn-sm btn-primary"><i class="fa fa-folder-open"></i> Detail</button>',
                            cellClick: (event, cell) => {
                                const medrec = cell.getRow().getData().Medrec;
                                if (medrec) {
                                    this.$router.push(`/riwayat-pasien/${medrec}`);
                                }
                            },
                        },
                    ],
                },
                this.$store
            );

            this.table.on("dataLoading", () => (this.is_loading = true));
            this.table.on("dataLoaded", () => (this.is_loading = false));
            this.table.on("dataLoadError", () => {
                this.is_loading = false;
                this.$swal({
                    icon: "error",
                    title: "Gagal memuat data",
                    text: "Data pasien tidak dapat diambil. Silakan coba lagi.",
                });
            });
        },

        async applyFilter() {
            if (this.invalid_date_range || !this.table) return;

            this.is_loading = true;

            try {
                await this.table.setData(this.buildApiUrl());
            } finally {
                this.is_loading = false;
            }
        },

        resetFilter() {
            this.filter = defaultFilter();
            this.selected = { poli: null, dokter: null };
            this.options.dokter = [];
            this.applyFilter();
        },

        onPoliChange(option) {
            this.filter.poli = option ? option.value : "";

            // Daftar dokter mengikuti poli yang dipilih, seperti di ERM.
            this.selected.dokter = null;
            this.filter.dokter = "";
            this.options.dokter = [];
        },

        onDokterChange(option) {
            this.filter.dokter = option ? option.value : "";
        },

        fetchPoli(search) {
            this.axios
                .get("/api/riwayat-pasien/poli", { params: { search } })
                .then(({ data }) => {
                    this.options.poli = (data.data || []).map((item) => ({
                        value: item.KDPoli,
                        label: item.NMPoli,
                    }));
                })
                .catch(() => (this.options.poli = []));
        },

        fetchDokter(search) {
            this.axios
                .get("/api/riwayat-pasien/dokter", {
                    params: { search, kd_poli: this.filter.poli },
                })
                .then(({ data }) => {
                    this.options.dokter = (data.data || []).map((item) => ({
                        value: item.KdDoc,
                        label: item.NmDoc,
                    }));
                })
                .catch(() => (this.options.dokter = []));
        },

        formatTanggal(value) {
            if (!value) return "-";
            return moment(String(value)).format("DD MMM YYYY");
        },
    },
};
</script>

<style scoped>
.table-responsive-wrapper {
    width: 100%;
    overflow-x: auto;
}

.table-responsive-wrapper >>> .tabulator {
    min-width: 1100px;
}

.table-loading {
    position: absolute;
    inset: 0;
    z-index: 10;
    background: rgba(255, 255, 255, 0.75);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    border-radius: 0.375rem;
}
</style>
